import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CalendarDays, ChevronDown, Search, X } from 'lucide-react';
import { useCodigosContables } from '@/hooks/useCodigosContables';

interface FilterBarProps {
  filters: Record<string, string>;
  onChange: (filters: Record<string, string>) => void;
}

// Generar opciones de meses (todo 2026 + 2025)
function generateMonthOptions() {
  const months = [];
  const years = [2026, 2025];

  for (const year of years) {
    for (let month = 11; month >= 0; month--) {
      const date = new Date(year, month, 1);
      const value = `${year}-${String(month + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
      const labelCapitalized = label.charAt(0).toUpperCase() + label.slice(1);
      const shortLabel = date.toLocaleDateString('es-CO', { month: 'short' });
      const shortCapitalized = shortLabel.charAt(0).toUpperCase() + shortLabel.slice(1).replace('.', '');

      months.push({ value, label: labelCapitalized, shortLabel: shortCapitalized, year });
    }
  }

  months.sort((a, b) => b.value.localeCompare(a.value));
  return months;
}

const monthOptions = generateMonthOptions();

export function FilterBar({ filters, onChange }: FilterBarProps) {
  const { data: usuariosData } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api.getUsuarios(),
  });

  const { data: proveedoresData } = useQuery({
    queryKey: ['proveedores'],
    queryFn: () => api.getProveedores(),
  });

  const { todos: codigosContables, activos: codigosContablesActivos } = useCodigosContables();
  const [codigoSearch, setCodigoSearch] = useState('');

  const handleChange = (key: string, value: string) => {
    const newFilters = { ...filters };
    if (value === 'all') {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }
    onChange(newFilters);
  };

  const selectedMeses = filters.meses ? filters.meses.split(',') : [];

  const handleMonthToggle = (monthValue: string) => {
    const newFilters = { ...filters };
    const current = new Set(selectedMeses);

    if (current.has(monthValue)) {
      current.delete(monthValue);
    } else {
      current.add(monthValue);
    }

    if (current.size === 0) {
      delete newFilters.meses;
    } else {
      newFilters.meses = Array.from(current).sort().join(',');
    }

    // Limpiar filtros de fecha legacy
    delete newFilters.fecha_desde;
    delete newFilters.fecha_hasta;
    delete newFilters.mes;

    onChange(newFilters);
  };

  const handleClearMeses = () => {
    const newFilters = { ...filters };
    delete newFilters.meses;
    delete newFilters.fecha_desde;
    delete newFilters.fecha_hasta;
    delete newFilters.mes;
    onChange(newFilters);
  };

  const getMesesLabel = () => {
    if (selectedMeses.length === 0) return 'Meses';
    if (selectedMeses.length === 1) {
      const opt = monthOptions.find(m => m.value === selectedMeses[0]);
      return opt?.label || 'Meses';
    }
    return `${selectedMeses.length} meses`;
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Filtro de Meses (multi-select) */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-[160px] justify-between font-normal">
            <div className="flex items-center gap-2 truncate">
              <CalendarDays className="h-4 w-4 shrink-0" />
              <span className="truncate">{getMesesLabel()}</span>
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[220px] max-h-[320px] overflow-y-auto" align="start">
          {selectedMeses.length > 0 && (
            <>
              <DropdownMenuCheckboxItem
                checked={false}
                onSelect={(e) => { e.preventDefault(); handleClearMeses(); }}
              >
                Todos los meses
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
            </>
          )}
          {[2026, 2025].map((year) => (
            <div key={year}>
              <DropdownMenuLabel className="text-xs text-muted-foreground">{year}</DropdownMenuLabel>
              {monthOptions
                .filter((m) => m.year === year)
                .map((option) => (
                  <DropdownMenuCheckboxItem
                    key={option.value}
                    checked={selectedMeses.includes(option.value)}
                    onSelect={(e) => { e.preventDefault(); handleMonthToggle(option.value); }}
                  >
                    {option.label}
                  </DropdownMenuCheckboxItem>
                ))}
              <DropdownMenuSeparator />
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Select
        value={filters.tipo_documento || 'all'}
        onValueChange={(value) => handleChange('tipo_documento', value)}
      >
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Tipo Doc." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tipo Documento</SelectItem>
          <SelectItem value="01">Factura de Venta</SelectItem>
          <SelectItem value="91">Nota Credito</SelectItem>
          <SelectItem value="92">Nota Debito</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.estado?.includes(',') ? 'all' : (filters.estado || 'all')}
        onValueChange={(value) => handleChange('estado', value)}
      >
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="Estados" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Estados</SelectItem>
          <SelectItem value="pendiente">Pendiente</SelectItem>
          <SelectItem value="aprobada">Aprobada</SelectItem>
          <SelectItem value="rechazada">Rechazada</SelectItem>
          <SelectItem value="en_espera">En Espera</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.proveedor_id || 'all'}
        onValueChange={(value) => handleChange('proveedor_id', value)}
      >
        <SelectTrigger className="w-[170px]">
          <SelectValue placeholder="Proveedores" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Proveedores</SelectItem>
          {proveedoresData?.proveedores?.map((p: any) => (
            <SelectItem key={p.id} value={p.id}>
              {p.razon_social}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.usuario_asignado_id || 'all'}
        onValueChange={(value) => handleChange('usuario_asignado_id', value)}
      >
        <SelectTrigger className="w-[150px]">
          <SelectValue placeholder="Usuarios" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Usuarios</SelectItem>
          {usuariosData?.usuarios?.map((u: any) => (
            <SelectItem key={u.id} value={u.id}>
              {u.nombre}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.erp || 'all'}
        onValueChange={(value) => handleChange('erp', value)}
      >
        <SelectTrigger className="w-[120px]">
          <SelectValue placeholder="ERP" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">ERP</SelectItem>
          <SelectItem value="1">Con ERP</SelectItem>
          <SelectItem value="0">Sin ERP</SelectItem>
        </SelectContent>
      </Select>

      {/* Filtro por código contable (subtipo_factura) */}
      <DropdownMenu onOpenChange={(open) => { if (!open) setCodigoSearch(''); }}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-[180px] justify-between font-normal">
            <span className="truncate">
              {filters.subtipo_factura
                ? codigosContables.find(i => i.codigo === filters.subtipo_factura)?.nombre || filters.subtipo_factura
                : 'Código Contable'}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[320px]" align="start">
          <div className="p-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Buscar código o nombre..."
                value={codigoSearch}
                onChange={(e) => setCodigoSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <DropdownMenuSeparator />
          {filters.subtipo_factura && (
            <>
              <DropdownMenuItem
                onClick={() => handleChange('subtipo_factura', 'all')}
                className="cursor-pointer text-muted-foreground"
              >
                <X className="h-3.5 w-3.5 mr-2" />
                <span className="text-sm">Limpiar filtro</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <div className="max-h-[280px] overflow-y-auto">
            {codigosContablesActivos
              .filter((item) => {
                const s = codigoSearch.toLowerCase();
                return !s || item.codigo.toLowerCase().includes(s) || item.nombre.toLowerCase().includes(s);
              })
              .slice(0, 50)
              .map((item) => (
                <DropdownMenuItem
                  key={item.codigo}
                  onClick={() => handleChange('subtipo_factura', item.codigo)}
                  className="cursor-pointer"
                >
                  <span className="font-mono text-xs text-blue-600 mr-2 shrink-0">{item.codigo}</span>
                  <span className="text-sm truncate">{item.nombre}</span>
                </DropdownMenuItem>
              ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
